import { AuthorizationModel } from "../../authentication/models/authorization-model";
import { backboneModelProperties } from "../../decorators/backbone-properties";
import { prop } from "../../decorators/property";
import { AppSettings } from "../../app-settings";
import { autoinject, transient, Container } from "aurelia-dependency-injection";
import { UserSettingsModel } from "../../settings/models/user-settings";
import * as d3 from "d3";

interface ISizeAndPositionOptions {
    x: number;
    y: number;
    r: number;
    opacity?: number;
    animate?: boolean;
}

@backboneModelProperties({
    defaults : {
       CitingNodes: [],
       CitedNodes: []
    },
    urlRoot : `${AppSettings.GraphApiUrlPrefix}precedentmap`,
    idAttribute: "DocKey",
    relations: [
            {
                type: Backbone.Many,
                key: "CitedNodes",
                relatedModel: PrecedentMapModel
            },
            {
                type: Backbone.Many,
                key: "CitingNodes",
                relatedModel: PrecedentMapModel
            }
        ]
})
@autoinject
@transient()
export class PrecedentMapModel extends AuthorizationModel {
    @prop() public LayerDataLoaded: boolean;
    @prop() public Display: boolean;
    @prop() public CommonRelationships: number;
    @prop() public CitedNodes: Backbone.Collection<PrecedentMapModel>;
    @prop() public CitingNodes: Backbone.Collection<PrecedentMapModel>;
    @prop() public SizeAndPosition: ISizeAndPositionOptions;
    @prop() public SvgElement: any;
    @prop() public TreatmentClass: string;
    @prop() public TreatmentDescription: string;
    @prop() public Title: string;
    @prop() public Year: string;
    @prop() public TreatmentType: number;
    @prop() public TreatmentLabel: string;
    @prop() public FriendlyUrlTitle: string;
    @prop() public DefaultDirection: string;
    @prop() public DocKey: string;
    @prop() public Id: number;
    @prop() public mapCenterX: number;
    @prop() public mapCenterY: number;
    @prop() public mainRadius: number;
    @prop() public LabelGroup: any;
    @prop() public FilteredNodesCount: number;
    @prop() public TotalNodesCount: number;
    @prop() public ShowAllJurisdictions = false;

    constructor(options?) {
        super(options);
    }

    public get UniqueId() {
        const id = this.get("UniqueId");
        if (!id) {
            this.set("UniqueId", this.generateUniqueId());
        }
        return this.get("UniqueId");
    }

    public set UniqueId(unique) {
        this.set("UniqueId", unique);
    }

    public get AllRelations(): PrecedentMapModel[] {
        return this.CitedNodes.models.concat(this.CitingNodes.models);
    }

    public setDisplayRelations(displayTypes): void {
        this.CitingNodes.each((citingModel) => {
            const treatment = citingModel.TreatmentType;
            citingModel.Display = (displayTypes.indexOf(treatment) > -1);
        });
        this.CitedNodes.each((citedModel) => {
            const treatment = citedModel.TreatmentType;
            citedModel.Display = (displayTypes.indexOf(treatment) > -1);
        });
    }

    public get DisplayCitedNodes(): Backbone.Collection<PrecedentMapModel> {
        const collection = new Backbone.Collection<PrecedentMapModel>();
        const allCiting = this.CitedNodes.where({ Display: true });
        collection.add(allCiting);
        return collection;
    }

    public get DisplayCitingNodes(): Backbone.Collection<PrecedentMapModel> {
        const collection = new Backbone.Collection<PrecedentMapModel>();
        const allCiting = this.CitingNodes.where({ Display: true });
        collection.add(allCiting);
        return collection;
    }

    public generateUniqueId() {
        const s4 = () => {
            return Math.floor((1 + Math.random()) * 0x10000)
                .toString(16)
                .substring(1);
        };
        return s4() + s4() + "-" + s4() + "-" + s4() + "-" +
            s4() + "-" + s4() + s4() + s4();
    }

    public computeCitedNodesPosition(mainX, mainY, mainR) {
        const nodeSet = this.DisplayCitedNodes;

        // create a temporary data object to pass to d3 circle packer
        const data: any = {};
        data.children = [];
        nodeSet.each((node) => {
            const dataObject: any = {};
            dataObject.id = node.DocKey;
            dataObject.size = this.normalizeNumberOfCommonRelationships(node.CitedNodes.size() + node.CitingNodes.size());
            data.children.push(dataObject);
        });
        const maxArea = Math.PI * mainR * mainR;
        const count = Math.max(nodeSet.size(), 30);
        const ratio = nodeSet.size() / count;
        const newArea = maxArea * ratio;
        const newRadius = Math.sqrt(newArea / Math.PI);
        const diameter = 1.9 * newRadius;
        const pack = d3.layout.pack().padding(50).size([diameter, diameter]).value((d) => (d as any).size).sort((a, b) => b.value - a.value);
        pack.nodes(data);
        data.children = data.children ? data.children : [];
        data.children.forEach((circle) => {
            circle.x = mainX + circle.x - (newRadius);
            circle.y = mainY + circle.y - (newRadius);
        });
        nodeSet.each((node: any) => {
            const pos = data.children.filter((o) => o.id === node.DocKey)[0];
            node.SizeAndPosition = pos;
        });
    }

    // TODO: TIDY THIS UP!!!
    public computeCitingNodesPosition(mainX, mainY, mainR) {
        const nodeSet = this.DisplayCitingNodes;
        const stDevNetworkConnectionsPerNode = 7;
        const maxOffset = 1; // increasing this will penalise large nodes in smaller networks
        const averageOffset = .5; // increasing this will scale up node sizes in general

        let highestConnectionCount = 1;
        let totalConnectionCount = 1;
        let totalRootConnectionCount = 1;
        const arc = (1.75 * Math.PI);
        const averageAngle = arc / nodeSet.length;

        nodeSet.forEach((node: any) => {
            const relations = this.normalizeNumberOfCommonRelationships(node.CitedNodes.size() + node.CitingNodes.size());
            if (relations > highestConnectionCount) {
                highestConnectionCount = relations;
            }
            const sqr = Math.sqrt(relations);
            totalConnectionCount += relations;
            totalRootConnectionCount += sqr;
        });

        const averageNetworkConnectionsPerNode = totalConnectionCount / nodeSet.length;
        const f1q = 1.0 / ((highestConnectionCount + maxOffset) * 1.5);
        const f2q = 1.0 / (stDevNetworkConnectionsPerNode + averageOffset);

        let angleSoFar = 0.4;
        const radius = mainR * 1.4;
        let count = 0;
        let previousSize = 0;
        nodeSet.comparator = (model) => {
            return model.get("Year");
        };
        nodeSet.sort();
        nodeSet.forEach((node: any) => {
            const score = this.normalizeNumberOfCommonRelationships(node.get("CitedNodes").size() + node.get("CitingNodes").size());
            const rootScore = Math.sqrt(score);
            const f1 = Math.min(.5, score * f1q);
            const f2 = Math.max(0, Math.min(.5, ((score - averageNetworkConnectionsPerNode + averageOffset) * f2q)));
            let size = 35 * (f1 + f2) + 20;
            size = Math.min((score * 3) + 30, size);
            const proportionalAngle = (rootScore / totalRootConnectionCount) * arc;
            const angleIncrease = proportionalAngle / 2;
            const xPos = (mainX) - (radius * Math.sin(angleSoFar + angleIncrease));
            const yPos = (mainY) + (radius * Math.cos(angleSoFar + angleIncrease));
            node.SizeAndPosition = {x: xPos, y: yPos, r: size};
            angleSoFar += proportionalAngle;
            count++;
            previousSize = size;
        });

        return nodeSet;
    }

    public loadData() {
        const params = $.param({ ShowAllJurisdictions: this.ShowAllJurisdictions });
        return new Promise((resolve) => {
            this.fetch({
                data: params,
                headers: {
                    UserSettings: this.getUserSettingsForServer()
                },
                success: () => {
                    resolve();
                }
            });
        });
    }

    private getUserSettingsForServer() {
        const model: UserSettingsModel = Container.instance.get(UserSettingsModel);
        return JSON.stringify(UserSettingsModel.GetSettings());
    }

    private normalizeNumberOfCommonRelationships(num: number) {
        if (typeof (num) === "undefined" || num === 0) {
            num = 1;
        } else {
            num++;
        }
        return num;
    }
}



// WEBPACK FOOTER //
// ./~/tslint-loader??ref--0!./src/precedent-map/models/precedent-map.ts